import type { DeliverableContent, DeliverableKind } from '../types/session-task.types';

export const DELIVERABLE_TEMPLATE_ID = 'session-task-deliverable';
export const DELIVERABLE_OUTPUT_TYPES = new Set(['html_artifact', 'pdf_document', 'video', 'image']);
export const MAX_ZIP_BINARY_BYTES = 50 * 1024 * 1024;

const KIND_LABELS: Record<DeliverableKind, string> = {
  report: 'Report',
  film: 'Film',
  document: 'Document',
  other: 'Other',
};

export const deliverableKindLabel = (kind?: string): string =>
  (kind && KIND_LABELS[kind as DeliverableKind]) || 'Deliverable';

export const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const parseDeliverableContent = (content: any): DeliverableContent => {
  if (!content) return {};
  if (typeof content === 'string') {
    try {
      const parsed = JSON.parse(content);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof content === 'object' ? content : {};
};

const EXT_BY_CONTENT_TYPE: Record<string, string> = {
  'text/html': 'html',
  'application/pdf': 'pdf',
  'video/mp4': 'mp4',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'application/zip': 'zip',
};

const EXT_BY_OUTPUT_TYPE: Record<string, string> = {
  html_artifact: 'html',
  pdf_document: 'pdf',
  video: 'mp4',
  image: 'png',
};

export const extensionFor = (outputType: string, content: DeliverableContent): string => {
  const fromName = content.fileName?.match(/\.([a-z0-9]{1,5})$/i)?.[1];
  if (fromName) return fromName.toLowerCase();
  const fromKey = content.s3Key?.match(/\.([a-z0-9]{1,5})$/i)?.[1];
  if (fromKey) return fromKey.toLowerCase();
  const ct = content.contentType?.split(';')[0].trim().toLowerCase();
  return (ct && EXT_BY_CONTENT_TYPE[ct]) || EXT_BY_OUTPUT_TYPE[outputType] || 'bin';
};

/** File name with its real extension, sanitised for zip entries. */
export const deliverableFileName = (outputType: string, content: DeliverableContent, fallback = 'output'): string => {
  const ext = extensionFor(outputType, content);
  const raw = content.fileName || content.title || fallback;
  const base = raw.replace(/[/\\?%*:|"<>]/g, '-');
  return base.toLowerCase().endsWith(`.${ext}`) ? base : `${base}.${ext}`;
};

/** True when the output must be handled as bytes, not text. */
export const isBinaryOutput = (outputType: string, content: DeliverableContent): boolean => {
  if (outputType === 'pdf_document' || outputType === 'video' || outputType === 'image') return true;
  const ct = content.contentType?.toLowerCase();
  if (ct) return !(ct.startsWith('text/') || ct.includes('json') || ct.includes('markdown'));
  const ext = extensionFor(outputType, content);
  return ['pdf', 'mp4', 'png', 'jpg', 'jpeg', 'zip', 'mov', 'webm', 'gif'].includes(ext);
};

/** Fetch a URL as a blob and trigger a browser download with the given name. */
export const downloadUrlAsFile = async (url: string, fileName: string): Promise<void> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  saveBlobAsFile(await res.blob(), fileName);
};

export const saveBlobAsFile = (blob: Blob, fileName: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
};
